import { ICPaasConfig, VoiceCallOptions, VoiceState, CallSummary } from './types';
import type { VoiceDisconnect, VoiceHello, VoiceServerMessage } from './protocol';
import { AuthManager } from './auth';
import { EventEmitter } from './events';
import { MicCapture, AudioPlayback, CAPTURE_RATE } from './audio';

/**
 * Control frames the voice bridge sends (server/icpaas-ai/voiceBridge.ts),
 * typed by the shared protocol. Types this client has no use for (e.g.
 * caller_emotion), and any a newer server adds, are ignored.
 */
type BridgeMessage = VoiceServerMessage;

/** getUserMedia rejects with DOMExceptions, but a caller could throw anything. */
function toError(value: unknown, fallback: string): Error {
  if (value instanceof Error) return value;
  return new Error(typeof value === 'string' ? value : fallback);
}

/**
 * Voice call manager — owns the /ws/session lifecycle plus mic capture and
 * playback.
 */
export class VoiceManager {
  private config: ICPaasConfig;
  private auth: AuthManager;
  private events: EventEmitter;
  private ws: WebSocket | null = null;
  private currentState: VoiceState = 'idle';
  private mic = new MicCapture();
  private playback = new AudioPlayback();
  private endTimer: ReturnType<typeof setTimeout> | null = null;
  private startedAt = 0;
  private options: VoiceCallOptions = {};

  constructor(config: ICPaasConfig, auth: AuthManager, events: EventEmitter) {
    this.config = config;
    this.auth = auth;
    this.events = events;
  }

  /** Analyser for the caller's mic, for driving a visualiser. */
  get micAnalyser(): AnalyserNode | null {
    return this.mic.analyser;
  }

  /** Analyser for the agent's voice, for driving a visualiser. */
  get aiAnalyser(): AnalyserNode | null {
    return this.playback.analyser;
  }

  async startCall(options: VoiceCallOptions = {}): Promise<void> {
    if (this.ws) throw new Error('Voice call already in progress');

    this.options = options;
    this.setState('connecting');

    // Mic first: if permission is refused there is no point opening a socket,
    // and the caller gets a useful error instead of a silent dead call.
    try {
      await this.mic.start((pcm) => {
        if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(pcm);
      });
    } catch (error) {
      this.setState('idle');
      const err = toError(error, 'Microphone unavailable');
      this.events.emit('error', err);
      throw err;
    }

    const baseURL = this.auth.getBaseURL();
    let wsURL = baseURL.replace(/^http/, 'ws') + '/ws/session';
    // A browser cannot set headers on a WebSocket handshake, so the key has to
    // ride in the query string. It is the same credential either way.
    if (this.config.apiKey) {
      wsURL += `?apiKey=${encodeURIComponent(this.config.apiKey)}`;
    }

    this.log('Connecting to voice bridge');
    this.events.emit('connecting');

    const socket = new WebSocket(wsURL);
    socket.binaryType = 'arraybuffer';
    this.ws = socket;
    this.startedAt = Date.now();

    socket.onopen = () => {
      this.log('WebSocket connected');
      const hello: VoiceHello = {
        type: 'hello',
        inputSampleRateHz: CAPTURE_RATE,
        // With an agent, a defaulted persona would force its IND voice channel;
        // leaving it out lets the agent's own default language stand.
        persona: options.persona || (options.agentId ? undefined : 'IND_Male'),
        agentId: options.agentId || undefined,
        language: options.language || undefined,
        localTimeIso: new Date().toISOString(),
      };
      socket.send(JSON.stringify(hello));
      this.events.emit('connected');
      this.events.emit('call-started', { startedAt: this.startedAt });
    };

    socket.onmessage = (event) => {
      // Binary frames are agent audio; everything else is a JSON control message.
      if (typeof event.data !== 'string') {
        this.playback.push(event.data as ArrayBuffer);
        return;
      }
      try {
        this.handleMessage(JSON.parse(event.data));
      } catch (error) {
        console.error('[ICPaaS SDK] Failed to parse message:', error);
      }
    };

    socket.onerror = (event) => {
      // The browser deliberately hides the cause of a socket error; onclose
      // follows with the code, so this only has to say that it happened.
      console.error('[ICPaaS SDK] WebSocket error:', event);
      this.events.emit('error', new Error('Voice connection error'));
    };

    socket.onclose = (event) => {
      this.log(`WebSocket closed (${event.code})`);
      if (event.code === 1008) {
        this.events.emit('error', new Error('Voice bridge rejected the API key'));
      }
      this.teardown();
      this.events.emit('disconnected', { code: event.code });
      const summary: CallSummary = { startedAt: this.startedAt, endedAt: Date.now(), durationSec: this.durationSec() };
      this.events.emit('call-ended', summary);
      this.options.onCallEnd?.(summary);
    };
  }

  /** End the call immediately. */
  endCall(): void {
    if (!this.ws) return;
    this.log('Ending voice call');
    try {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'disconnect' } satisfies VoiceDisconnect));
      }
      this.ws.close();
    } catch (error) {
      console.error('[ICPaaS SDK] Error ending call:', error);
    }
    // onclose does the teardown, so state stays consistent whichever side hangs up.
  }

  /**
   * The agent said goodbye: let whatever audio is already queued finish, then
   * tear down. Cutting immediately clips the farewell mid-word.
   */
  private finishCall(): void {
    if (!this.ws || this.endTimer) return;
    this.mic.stop(); // stop listening straight away
    const wait = Math.min(this.playback.remainingMs() + 250, 10000);
    this.endTimer = setTimeout(() => {
      this.endTimer = null;
      this.endCall();
    }, wait);
  }

  private teardown(): void {
    if (this.endTimer) { clearTimeout(this.endTimer); this.endTimer = null; }
    this.mic.stop();
    this.playback.stop();
    this.ws = null;
    this.setState('idle');
  }

  private handleMessage(msg: BridgeMessage): void {
    switch (msg.type) {
      case 'ready':
        this.playback.start(msg.outputSampleRateHz);
        this.setState('listening');
        this.events.emit('ready', { outputSampleRateHz: msg.outputSampleRateHz, provider: msg.provider });
        break;

      case 'status':
        if (msg.state === 'disconnected') { this.finishCall(); return; }
        this.setState(msg.state);
        break;

      case 'transcript_user':
        // Partial transcripts arrive repeatedly; only finals are real turns.
        if (msg.final && msg.text) {
          this.events.emit('message', { channel: 'voice', role: 'user', text: msg.text, final: true });
        }
        break;

      case 'transcript_ai_delta':
        this.events.emit('message', { channel: 'voice', role: 'agent', text: msg.text, delta: true });
        break;

      case 'barge_in':
        this.playback.flush();
        break;

      case 'error':
        console.error('[ICPaaS SDK] Voice error:', msg.message);
        this.events.emit('error', new Error(msg.message));
        break;

      case 'open_url':
        // Emitted, not opened: a popup with no user gesture behind it is blocked
        // anyway, so the host page should surface a button.
        this.events.emit('open-url', { url: msg.url });
        break;

      default:
        break;
    }
  }

  private setState(state: VoiceState): void {
    if (this.currentState === state) return;
    this.currentState = state;
    this.events.emit('state-change', state);
    // Here rather than only on server 'status' frames, so the callback also
    // sees 'connecting' and the final 'idle', as the event does.
    this.options.onStateChange?.(state);
  }

  private durationSec(): number {
    return this.startedAt ? Math.round((Date.now() - this.startedAt) / 1000) : 0;
  }

  private log(message: string): void {
    if (this.config.debug) console.log(`[ICPaaS SDK] ${message}`);
  }

  getState(): VoiceState {
    return this.currentState;
  }

  isActive(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}
